import { storageService } from '@/services/storage'

import type {
  Customer,
  CustomerFilters,
  CustomerInput,
  CustomerListParams,
  CustomerListResult,
  CustomerSortBy,
} from '../types'
import { createFakeCustomers } from '../utils/mock-customers'

const STORAGE_KEY = 'masterhub.customers.v1'

/**
 * CustomerRepository — SPA persistence (localStorage) mirroring Prisma Customer.
 * Soft delete via ativo=false. In-memory fallback for Node/tests.
 */
export class CustomerRepository {
  private memory: Customer[] | null = null

  private ensureSeed(): Customer[] {
    const existing = this.readAll()
    if (existing.length > 0) return existing
    const seeded = createFakeCustomers(30)
    this.writeAll(seeded)
    return seeded
  }

  listAll(): Customer[] {
    return this.ensureSeed()
  }

  findById(id: string): Customer | null {
    return this.listAll().find((c) => c.id === id) ?? null
  }

  create(input: CustomerInput): Customer {
    const now = new Date().toISOString()
    const customer: Customer = {
      ...input,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
    }
    const all = this.listAll()
    this.writeAll([customer, ...all])
    return customer
  }

  update(id: string, input: Partial<CustomerInput>): Customer {
    const all = this.listAll()
    const index = all.findIndex((c) => c.id === id)
    if (index < 0) throw new Error('Cliente não encontrado')

    const current = all[index]!
    const updated: Customer = {
      ...current,
      ...input,
      id: current.id,
      createdAt: current.createdAt,
      updatedAt: new Date().toISOString(),
    }
    all[index] = updated
    this.writeAll(all)
    return updated
  }

  softDelete(id: string): Customer {
    return this.update(id, { ativo: false })
  }

  duplicate(id: string): Customer {
    const source = this.findById(id)
    if (!source) throw new Error('Cliente não encontrado')

    const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = source
    return this.create({
      ...rest,
      nome: `${source.nome} (cópia)`,
      ativo: true,
    })
  }

  query(params: CustomerListParams): CustomerListResult {
    const filtered = filterCustomers(this.listAll(), params.filters)
    const sorted = sortCustomers(filtered, params.sortBy)
    const total = sorted.length
    const totalPages = Math.max(1, Math.ceil(total / params.pageSize))
    const page = Math.min(Math.max(1, params.page), totalPages)
    const start = (page - 1) * params.pageSize
    const items = sorted.slice(start, start + params.pageSize)

    return { items, total, page, pageSize: params.pageSize, totalPages }
  }

  resetWithSeed(total = 30): void {
    this.writeAll(createFakeCustomers(total))
  }

  private readAll(): Customer[] {
    if (typeof window === 'undefined') {
      return this.memory ?? []
    }
    return storageService.getJSON<Customer[]>(STORAGE_KEY, [])
  }

  private writeAll(customers: Customer[]): void {
    if (typeof window === 'undefined') {
      this.memory = customers
      return
    }
    storageService.setJSON(STORAGE_KEY, customers)
  }
}

export const customerRepository = new CustomerRepository()

export function filterCustomers(customers: Customer[], filters: CustomerFilters): Customer[] {
  const q = filters.query.trim().toLowerCase()

  return customers.filter((c) => {
    if (filters.status === 'active' && !c.ativo) return false
    if (filters.status === 'inactive' && c.ativo) return false
    if (filters.tipoPessoa && filters.tipoPessoa !== 'all' && c.tipoPessoa !== filters.tipoPessoa) {
      return false
    }
    if (filters.cidade && c.cidade !== filters.cidade) return false

    if (!q) return true

    const haystack = [
      c.nome,
      c.empresa,
      c.cpfCnpj,
      c.telefone,
      c.whatsapp,
      c.email,
      c.cidade,
      c.estado,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(q)
  })
}

export function sortCustomers(customers: Customer[], sortBy: CustomerSortBy): Customer[] {
  const sorted = [...customers]
  switch (sortBy) {
    case 'nome_asc':
      return sorted.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    case 'nome_desc':
      return sorted.sort((a, b) => b.nome.localeCompare(a.nome, 'pt-BR'))
    case 'createdAt_asc':
      return sorted.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    case 'cidade_asc':
      return sorted.sort((a, b) => (a.cidade ?? '').localeCompare(b.cidade ?? '', 'pt-BR'))
    case 'createdAt_desc':
    default:
      return sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }
}
